import React, { useState, useEffect } from "react";

// ============ AXIOS CONFIGURATION ============
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create API helper
const api = {
    post: async (url, data, config = {}) => {
        // spread config first, then override/extend headers
        const response = await fetch(url, {
            method: 'POST',
            ...config,
            headers: {
                'Content-Type': 'application/json',
                ...(config.headers || {})
            },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Network error' }));
            throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        return { data: await response.json() };
    }
};


export default function ConfigureTimerModal({ open, onClose, template }) {
    const [triggerAfter, setTriggerAfter] = useState(2);
    const [triggerUnit, setTriggerUnit] = useState("days");
    const [maxSends, setMaxSends] = useState(1);
    const [isRecurring, setIsRecurring] = useState(true);
    const [repeatInterval, setRepeatInterval] = useState(2);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sendingNow, setSendingNow] = useState(false);

    const [timerData, setTimerData] = useState({
        templateAction: '',
        recipientType: 'all',  // default to 'all'
        userEmail: ''
    });



    // ============ TEMPLATE CONFIGURATIONS ============
    const timerConfigurations = {
        'signup_no_purchase': {
            defaultTrigger: 2,
            defaultUnit: 'days',
            description: 'Follow-up after signup without purchase',
            isRecurring: true,
            defaultMaxSends: 3
        },
        'purchase_no_counselling': {
            defaultTrigger: 2,
            defaultUnit: 'days',
            description: 'Follow-up after purchase without counselling',
            isRecurring: true,
            defaultMaxSends: 5
        },
        'package_not_purchased': {
            defaultTrigger: 5,
            defaultUnit: 'minutes',
            description: 'Abandoned cart follow-up',
            isRecurring: false,
            defaultMaxSends: 1
        },
        'inactive_user': {
            defaultTrigger: 7,
            defaultUnit: 'days',
            description: 'Re-engage inactive users',
            isRecurring: false,
            defaultMaxSends: 1
        }
    };

    // ============ DEBUGGING HELPER ============
    const debugTemplate = () => {
        console.log('🔍 TEMPLATE DEBUG:');
        console.log('- template:', template);
        console.log('- typeof template:', typeof template);
        if (template && typeof template === 'object') {
            console.log('- template keys:', Object.keys(template));
            console.log('- template.action:', template.action);
            console.log('- template.name:', template.name);
        }
    };

    // ============ EFFECT FOR TEMPLATE SETUP ============
    useEffect(() => {
        if (open && template) {
            console.log('🔧 Setting up timer modal for template:', template);

            let templateAction;
            if (typeof template === 'string') {
                templateAction = template;
            } else if (template && template.action) {
                templateAction = template.action;
            }

            const config = timerConfigurations[templateAction];
            if (config) {
                setTriggerAfter(config.defaultTrigger);
                setTriggerUnit(config.defaultUnit);
                setIsRecurring(config.isRecurring);
                setMaxSends(config.defaultMaxSends);
                setRepeatInterval(config.defaultTrigger);
            }

            setError(''); // Clear any previous errors
        }
    }, [open, template]);


    // ============ SEND NOW ACTION ============

   const handleSendNow = async () => {
  try {
    setSendingNow(true);

    // Extract from `template` prop instead of `timerData`
    const templateAction = (
      typeof template === 'string'
        ? template
        : template?.action
    )?.trim();

    if (!templateAction) {
      throw new Error('templateAction is required');
    }

    const payload = {
      templateAction,
      recipientType: 'all', // Can extend to 'specific'
      userEmail: ''         // Optional, only needed if sending to one user
    };

    const API_BASE_URL =process.env.REACT_APP_API_URL;

    const response = await fetch(`${API_BASE_URL}/api/admin/email-timers/send-now`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Unknown error while sending email');
    }

    alert(result.message || 'Emails sent successfully!');
    console.log('✅ Send results:', result.details);
  } catch (err) {
    console.error('❌ Send now error:', err);
    alert(`Failed to send emails:\n${err.message}`);
  } finally {
    setSendingNow(false);
  }
};




    // ============ SAVE TIMER CONFIGURATION ============
   const handleSave = async () => {
    try {
        setLoading(true);
        setError('');

        console.log('💾 Saving timer configuration...');
        debugTemplate();

        let templateAction, templateName;

        if (typeof template === 'string') {
            templateAction = template;
            templateName = template;
        } else if (template && typeof template === 'object' && template.action) {
            templateAction = template.action;
            templateName = template.name || template.action;
        } else {
            console.error('❌ Invalid template data for save:', template);
            throw new Error(`Invalid template data. Received: ${JSON.stringify(template)}`);
        }

        if (!templateAction || templateAction.trim() === '') {
            throw new Error('Template action is required and cannot be empty');
        }

        if (!triggerAfter || triggerAfter <= 0) {
            throw new Error('Trigger time must be greater than 0');
        }

        const payload = {
            templateAction: templateAction.trim(),
            triggerAfter: Number(triggerAfter),
            triggerUnit: triggerUnit,
            targetCondition: templateAction.trim(),
            isActive: true,
            maxSends: Number(maxSends) || 1,
            repeatInterval: isRecurring ? Number(repeatInterval) || 1 : null,
            isRecurring: Boolean(isRecurring),
            description: `Auto-send ${templateName} after ${triggerAfter} ${triggerUnit}`
        };

        console.log('📤 Sending save payload:', payload);

        const response = await api.post(
            `${API_BASE_URL}/api/admin/email-timers`,
            payload
        );

        console.log('✅ Timer configured successfully:', response.data);
        alert('Email timer configured successfully!');
        onClose();

    } catch (err) {
        console.error('❌ Timer configuration error:', err);
        setError(`Failed to configure timer: ${err.message}`);
    } finally {
        setLoading(false);
    }
};


    // ============ TEST TIMER TRIGGER ============
   const handleTestTrigger = async () => {
  try {
    setLoading(true);
    setError('');
    debugTemplate();

    const templateAction =
      typeof template === 'string'
        ? template.trim()
        : template?.action?.trim();

    if (!templateAction) {
      throw new Error('Template action is required');
    }

    const { data } = await api.post(`${API_BASE_URL}/api/admin/test-timer-trigger`, {
      templateAction,
    });

    const { emailPreview, timerConfig, testUser } = data;

    alert(`✅ Timer test completed!\n\n📧 Sent to ${testUser.email}\nTemplate: ${emailPreview.template}\nSubject: ${emailPreview.subject}`);
  } catch (err) {
    console.error('❌ Timer test error:', err);
    setError(`Timer test failed: ${err.message}`);
    alert(`Timer test failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
};


    // ============ RENDER GUARDS ============
    if (!open) return null;

    // Handle template validation
    let templateAction, templateName;
    if (typeof template === 'string') {
        templateAction = template;
        templateName = template;
    } else if (template && template.action) {
        templateAction = template.action;
        templateName = template.name || template.action;
    } else {
        return (
            <div className="ctm-modal-overlay">
                <div className="ctm-modal">
                    <h2>❌ Error</h2>
                    <p>Invalid template data. Please close and try again.</p>
                    <p><strong>Received:</strong> {JSON.stringify(template, null, 2)}</p>
                    <button onClick={onClose} className="ctm-btn-secondary">Close</button>
                </div>
            </div>
        );
    }

    const config = timerConfigurations[templateAction] || {};

    return (
        <div className="ctm-modal-overlay">
            <div className="ctm-modal">
                <h2>⏰ Configure Email Timer</h2>
                <p className="ctm-template-action">
                    <strong>Template:</strong> {templateName}<br />
                    <strong>Action:</strong> {templateAction}
                </p>

                {error && (
                    <div className="ctm-error-message">
                        <strong>❌ Error:</strong> {error}
                    </div>
                )}

                {config.description && (
                    <div className="ctm-info-box">
                        <strong>ℹ️ Description:</strong> {config.description}
                    </div>
                )}

                <div className="ctm-form-group">
                    <label htmlFor="triggerAfter">Send Email After *</label>
                    <div className="ctm-input-group">
                        <input
                            id="triggerAfter"
                            type="number"
                            min="1"
                            max="365"
                            value={triggerAfter}
                            onChange={(e) => setTriggerAfter(Math.max(1, parseInt(e.target.value || 1)))}
                            disabled={loading}
                            className="ctm-number-input"
                        />
                        <select
                            value={triggerUnit}
                            onChange={(e) => setTriggerUnit(e.target.value)}
                            disabled={loading}
                            className="ctm-select"
                        >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                        </select>
                    </div>
                    <small>Time to wait before sending the first email</small>
                </div>

                <div className="ctm-form-group">
                    <label htmlFor="maxSends">Maximum Emails per User *</label>
                    <select
                        id="maxSends"
                        value={maxSends}
                        onChange={(e) => setMaxSends(parseInt(e.target.value))}
                        disabled={loading}
                        className="ctm-select"
                    >
                        <option value="1">Send Once</option>
                        <option value="2">Send Twice</option>
                        <option value="3">Send 3 Times</option>
                        <option value="5">Send 5 Times</option>
                        <option value="999">Unlimited</option>
                    </select>
                    <small>How many times this email can be sent to each user</small>
                </div>

                <div className="ctm-form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={isRecurring}
                            onChange={(e) => setIsRecurring(e.target.checked)}
                            disabled={loading}
                        />
                        Enable Recurring Emails
                    </label>
                    <small>Send follow-up emails at regular intervals</small>
                </div>

                {isRecurring && (
                    <div className="ctm-form-group">
                        <label htmlFor="repeatInterval">Repeat Every</label>
                        <div className="ctm-input-group">
                            <input
                                id="repeatInterval"
                                type="number"
                                min="1"
                                max="30"
                                value={repeatInterval}
                                onChange={(e) => setRepeatInterval(Math.max(1, parseInt(e.target.value || 1)))}
                                disabled={loading}
                                className="ctm-number-input"
                            />
                            <span className="ctm-unit-label">{triggerUnit}</span>
                        </div>
                        <small>Interval between recurring emails</small>
                    </div>
                )}

                <div className="ctm-preview-section">
                    <h4>📋 Configuration Summary</h4>
                    <ul>
                        <li><strong>First email:</strong> {triggerAfter} {triggerUnit} after trigger event</li>
                        <li><strong>Maximum sends:</strong> {maxSends === 999 ? 'Unlimited' : maxSends} per user</li>
                        <li><strong>Recurring:</strong> {isRecurring ? `Yes, every ${repeatInterval} ${triggerUnit}` : 'No'}</li>
                        <li><strong>Trigger condition:</strong> {templateAction.replace(/_/g, ' ')}</li>
                    </ul>
                </div>

                <div className="ctm-modal-buttons">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="ctm-btn-secondary"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleTestTrigger}
                        disabled={loading || !templateAction}
                        className="ctm-btn-test"
                    >
                        {loading ? 'Testing...' : '🧪 Test Now'}
                    </button>

                    <button
                        onClick={handleSendNow}
                        disabled={loading || !templateAction}
                        className="ctm-btn-send"
                    >
                        {loading ? 'Sending...' : '📧 Send Now'}
                    </button>


                    <button
                        onClick={handleSave}
                        disabled={loading || !templateAction}
                        className="ctm-btn-primary"
                    >
                        {loading ? 'Saving...' : '💾 Save Timer'}
                    </button>
                </div>
            </div>

            <style>{`
                .ctm-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 9999;
                }
                
                .ctm-modal {
                    background: white;
                    padding: 24px;
                    border-radius: 12px;
                    width: 600px;
                    max-width: 90vw;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                }
                
                .ctm-modal h2 {
                    margin: 0 0 16px 0;
                    color: #1f2937;
                    font-size: 24px;
                }
                
                .ctm-template-action {
                    background: #f3f4f6;
                    padding: 12px;
                    border-radius: 6px;
                    font-size: 14px;
                    color: #6b7280;
                    margin-bottom: 20px;
                    border-left: 4px solid #3b82f6;
                }
                
                .ctm-error-message {
                    background: #fef2f2;
                    border: 1px solid #fca5a5;
                    color: #dc2626;
                    padding: 12px;
                    border-radius: 6px;
                    margin-bottom: 16px;
                    font-size: 14px;
                }
                
                .ctm-info-box {
                    background: #eff6ff;
                    border: 1px solid #bfdbfe;
                    color: #1e40af;
                    padding: 12px;
                    border-radius: 6px;
                    margin-bottom: 16px;
                    font-size: 14px;
                }
                
                .ctm-form-group {
                    margin-bottom: 20px;
                }
                
                .ctm-form-group label {
                    display: block;
                    margin-bottom: 6px;
                    font-weight: 600;
                    color: #374151;
                    font-size: 14px;
                }
                
                .ctm-input-group {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }
                
                .ctm-number-input, .ctm-select {
                    flex: 1;
                    padding: 10px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: border-color 0.2s;
                }
                
                .ctm-number-input:focus, .ctm-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                
                .ctm-unit-label {
                    font-size: 14px;
                    color: #6b7280;
                    min-width: 60px;
                }
                
                .ctm-form-group input[type="checkbox"] {
                    margin-right: 8px;
                    transform: scale(1.1);
                }
                
                .ctm-form-group small {
                    display: block;
                    margin-top: 4px;
                    color: #6b7280;
                    font-size: 12px;
                }
                
                .ctm-preview-section {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 16px;
                    margin: 20px 0;
                }
                
                .ctm-preview-section h4 {
                    margin: 0 0 12px 0;
                    color: #374151;
                    font-size: 16px;
                }
                
                .ctm-preview-section ul {
                    margin: 0;
                    padding-left: 20px;
                    color: #6b7280;
                }
                
                .ctm-preview-section li {
                    margin-bottom: 4px;
                }
                
                .ctm-modal-buttons {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 24px;
                    padding-top: 20px;
                    border-top: 1px solid #e5e7eb;
                }
                
                .ctm-modal-buttons button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 100px;
                }
                
                .ctm-modal-buttons button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .ctm-btn-secondary {
                    background-color: #6b7280;
                    color: white;
                }
                
                .ctm-btn-secondary:hover:not(:disabled) {
                    background-color: #4b5563;
                }
                
                .ctm-btn-test {
                    background-color: #f59e0b;
                    color: white;
                }
                
                .ctm-btn-test:hover:not(:disabled) {
                    background-color: #d97706;
                }
                
                .ctm-btn-primary {
                    background-color: #3b82f6;
                    color: white;
                }
                
                .ctm-btn-primary:hover:not(:disabled) {
                    background-color: #2563eb;
                }
            `}</style>
        </div>
    );
}